import { useMemo, useState } from "react";
import AppShell from "@/components/layout/AppShell";
import useAutoRefresh from "@/hooks/useAutoRefresh";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { MagnifyingGlass, X, ArrowClockwise, ImageSquare, PencilSimple, FloppyDisk, Warning } from "@phosphor-icons/react";
import StatusBadge from "@/components/common/StatusBadge";
import AuthenticatedImage from "@/components/common/AuthenticatedImage";
import { useAuth } from "@/context/AuthContext";

const MODULES = [
  { key: "gate",      label: "Gerbang",    photoFields: ["photo_in", "photo_out"], patchUrl: (id) => `/gate/${id}` },
  { key: "receiving", label: "Receiving",  photoFields: ["photos"], patchUrl: (id) => `/receiving/${id}` },
  { key: "delivery",  label: "Delivery",   photoFields: ["photos"], patchUrl: (id) => `/delivery/${id}` },
  { key: "weigh",     label: "Timbang",    photoFields: ["photo_truck", "photo_display"], patchUrl: (id) => `/weigh/${id}` },
  { key: "loading",   label: "Loading",    photoFields: ["photo_position", "photo_perintah", "photo_nozzle", "photo_during", "photo_finished", "photo_do"], patchUrl: (id) => `/loading/${id}` },
  { key: "opname",    label: "Opname",     photoFields: ["photo_tool", "photo_point", "photo_reading", "photo_tank"], patchUrl: (id) => `/opname/${id}` },
];

/** Editable field spec per module — must match backend whitelist (server.py). */
const EDIT_FIELDS = {
  gate: [
    { k: "access_type",  label: "Tipe",         type: "select", options: [{ v: "in", l: "Masuk" }, { v: "out", l: "Keluar" }] },
    { k: "vehicle_no",   label: "No. Plat",     type: "text" },
    { k: "driver_name",  label: "Driver",       type: "text" },
    { k: "company",      label: "Perusahaan",   type: "text" },
    { k: "reference_no", label: "No. Referensi",type: "text" },
    { k: "purpose",      label: "Keperluan",    type: "text" },
    { k: "phone",        label: "No. HP",       type: "text" },
    { k: "id_card_no",   label: "No. KTP",      type: "text" },
  ],
  receiving: [
    { k: "supplier",         label: "Supplier",         type: "text" },
    { k: "material",         label: "Barang",           type: "text" },
    { k: "category",         label: "Kategori",         type: "text" },
    { k: "po_no",            label: "No. PO",           type: "text" },
    { k: "estimated_date",   label: "Tanggal Estimasi", type: "date" },
    { k: "truck_no",         label: "No. Plat",         type: "text" },
    { k: "driver",           label: "Driver",           type: "text" },
    { k: "destination_tank", label: "Tank Tujuan",      type: "text" },
    { k: "estimated_qty",    label: "Estimasi Qty (L)", type: "number" },
    { k: "notes",            label: "Catatan",          type: "textarea" },
  ],
  delivery: [
    { k: "do_no",            label: "No. DO",           type: "text" },
    { k: "customer",         label: "Customer",         type: "text" },
    { k: "customer_address", label: "Alamat Customer",  type: "textarea" },
    { k: "product",          label: "Produk",           type: "text" },
    { k: "truck_no",         label: "No. Plat",         type: "text" },
    { k: "driver",           label: "Driver",           type: "text" },
    { k: "target_qty",       label: "Target Qty",       type: "number" },
    { k: "schedule_date",    label: "Tanggal Rencana",  type: "date" },
    { k: "notes",            label: "Catatan",          type: "textarea" },
  ],
  weigh: [
    { k: "weight",     label: "Berat (kg)",  type: "number" },
    { k: "truck_no",   label: "No. Plat",    type: "text" },
    { k: "operator",   label: "Operator",    type: "text" },
    { k: "notes",      label: "Catatan",     type: "textarea" },
  ],
  loading: [
    { k: "target_qty",    label: "Target Qty",   type: "number" },
    { k: "actual_qty",    label: "Actual Qty",   type: "number" },
    { k: "source_tank",   label: "Tank Sumber",  type: "text" },
    { k: "operator",      label: "Operator",     type: "text" },
    { k: "supervisor",    label: "Supervisor",   type: "text" },
    { k: "seal_numbers",  label: "No. Segel",    type: "text" },
    { k: "notes",         label: "Catatan",      type: "textarea" },
  ],
  opname: [
    { k: "measured_qty",  label: "Qty Terukur",  type: "number" },
    { k: "book_stock",    label: "Stok Buku",    type: "number" },
    { k: "product",       label: "Produk",       type: "text" },
    { k: "temperature",   label: "Suhu (°C)",    type: "number" },
    { k: "method",        label: "Metode",       type: "text" },
    { k: "petugas",       label: "Petugas",      type: "text" },
    { k: "supervisor",    label: "Supervisor",   type: "text" },
    { k: "notes",         label: "Catatan",      type: "textarea" },
  ],
};

/** Flatten row photos (arrays or strings) into a photos[] list of {label, src}. */
function extractPhotos(row, fields) {
  const out = [];
  for (const f of fields) {
    const v = row[f];
    if (!v) continue;
    if (Array.isArray(v)) {
      v.forEach((x, i) => x && out.push({ label: `${f}[${i + 1}]`, src: x }));
    } else if (typeof v === "string" && (v.startsWith("data:") || v.startsWith("photo:"))) {
      out.push({ label: f, src: v });
    }
  }
  return out;
}

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("id-ID") : "—");

export default function HistoryPage() {
  const { user } = useAuth();
  const role = user?.role;
  const isOwner = role === "admin" || role === "owner";
  const isOfficeEditor = role === "admin_kantor";  // dapat edit receiving/delivery saja
  const [tab, setTab] = useState("gate");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [editing, setEditing] = useState(null); // { row, module }
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  const currentMod = MODULES.find((m) => m.key === tab);
  const canEditCurrent = isOwner || (isOfficeEditor && (tab === "receiving" || tab === "delivery"));

  const load = async () => {
    setLoading(true);
    try {
      const params = { include_photos: true };  // history perlu foto untuk thumbnail
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (q.trim()) params.q = q.trim();
      const { data } = await api.get(`/history/${tab}`, { params });
      setRows(data.rows || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useAutoRefresh(load, { deps: [tab] });

  const reset = () => { setDateFrom(""); setDateTo(""); setQ(""); setTimeout(load, 0); };

  const openEdit = (row) => {
    const spec = EDIT_FIELDS[tab] || [];
    const init = {};
    for (const f of spec) init[f.k] = row[f.k] ?? "";
    setEditForm(init);
    setEditing({ row, module: currentMod });
  };

  const saveEdit = async () => {
    if (!editing) return;
    // Build payload only with changed values (non-empty)
    const payload = {};
    const spec = EDIT_FIELDS[tab] || [];
    for (const f of spec) {
      const val = editForm[f.k];
      if (val === undefined || val === null || val === "") continue;
      if (f.type === "number") {
        const n = Number(val);
        if (Number.isNaN(n)) {
          toast.error(`Field "${f.label}" harus angka`);
          return;
        }
        payload[f.k] = n;
      } else {
        payload[f.k] = val;
      }
    }
    if (Object.keys(payload).length === 0) {
      toast.error("Tidak ada perubahan untuk disimpan");
      return;
    }
    setSaving(true);
    try {
      const url = editing.module.patchUrl(editing.row.id);
      await api.patch(url, payload);
      toast.success("Perubahan disimpan & tercatat di Edit Log");
      setEditing(null);
      setEditForm({});
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const columns = useMemo(() => buildColumns(tab), [tab]);

  return (
    <AppShell
      title="Riwayat Semua Data"
      subtitle="Audit lengkap seluruh modul — termasuk foto bukti. Khusus admin, owner & admin kantor."
      actions={
        <Button onClick={load} data-testid="history-refresh" className="bg-slate-900 hover:bg-slate-800 h-10 gap-2">
          <ArrowClockwise size={15} weight="bold" /> <span className="hidden sm:inline">Muat Ulang</span>
        </Button>
      }
    >
      {/* Filter bar */}
      <div className="rounded-xl bg-white border border-slate-200 p-4 mb-4 shadow-sm">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Field label="Dari Tanggal">
            <Input data-testid="history-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-10" />
          </Field>
          <Field label="Sampai Tanggal">
            <Input data-testid="history-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-10" />
          </Field>
          <div className="lg:col-span-2">
            <Field label="Kata Kunci (supplier / customer / no. plat / no. DO / ref)">
              <div className="relative">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  data-testid="history-search"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && load()}
                  className="h-10 pl-9"
                  placeholder="ketik lalu Enter…"
                />
              </div>
            </Field>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-3">
          <Button variant="outline" onClick={reset} data-testid="history-reset" className="h-9">Reset</Button>
          <Button onClick={load} data-testid="history-apply" className="h-9 bg-sky-600 hover:bg-sky-700 gap-2">
            <MagnifyingGlass size={14} weight="bold" /> Cari
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="w-full flex flex-wrap h-auto p-1 bg-slate-100">
          {MODULES.map((m) => (
            <TabsTrigger
              key={m.key}
              value={m.key}
              data-testid={`history-tab-${m.key}`}
              className="flex-1 min-w-[100px] data-[state=active]:bg-slate-900 data-[state=active]:text-white"
            >
              {m.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {MODULES.map((m) => (
          <TabsContent key={m.key} value={m.key} className="mt-4">
            <div className="rounded-xl bg-white border border-slate-200 overflow-hidden shadow-sm">
              <div className="px-4 py-2.5 border-b border-slate-100 flex items-center gap-3 bg-slate-50">
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600">{m.label}</span>
                <span className="ml-auto text-[11px] font-mono text-slate-500" data-testid={`history-count-${m.key}`}>
                  {loading ? "Memuat…" : `${rows.length} baris`}
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-[12px]">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      {columns.map((c) => (
                        <th key={c.key} className="text-left px-3 py-2 font-heading text-[10.5px] font-bold uppercase tracking-wider text-slate-600">
                          {c.label}
                        </th>
                      ))}
                      <th className="text-right px-3 py-2 font-heading text-[10.5px] font-bold uppercase tracking-wider text-slate-600">Foto</th>
                      {canEditCurrent && (
                        <th className="text-right px-3 py-2 font-heading text-[10.5px] font-bold uppercase tracking-wider text-slate-600">Aksi</th>
                      )}
                    </tr>
                  </thead>
                  <tbody data-testid={`history-table-${m.key}`}>
                    {rows.length === 0 && !loading && (
                      <tr><td colSpan={columns.length + 1 + (canEditCurrent ? 1 : 0)} className="text-center py-10 text-slate-400 text-[13px]">Tidak ada data untuk filter ini.</td></tr>
                    )}
                    {rows.map((r) => {
                      const photos = extractPhotos(r, currentMod.photoFields);
                      return (
                        <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setDetail({ row: r, photos })}>
                          {columns.map((c) => (
                            <td key={c.key} className="px-3 py-2 align-top">{c.render ? c.render(r) : (r[c.key] ?? "—")}</td>
                          ))}
                          <td className="px-3 py-2 text-right">
                            {photos.length > 0 ? (
                              <div className="inline-flex gap-1 items-center">
                                {photos.slice(0, 3).map((p, i) => (
                                  <AuthenticatedImage
                                    key={i}
                                    src={p.src}
                                    alt={p.label}
                                    onClick={(e) => { e.stopPropagation(); setLightbox(p); }}
                                    className="w-8 h-8 rounded object-cover border border-slate-200 hover:ring-2 hover:ring-sky-500 cursor-zoom-in"
                                  />
                                ))}
                                {photos.length > 3 && (
                                  <span className="text-[10px] font-mono text-slate-500 ml-1">+{photos.length - 3}</span>
                                )}
                              </div>
                            ) : (
                              <span className="text-slate-300 inline-flex items-center gap-1 text-[11px]"><ImageSquare size={13} /> —</span>
                            )}
                          </td>
                          {canEditCurrent && (
                            <td className="px-3 py-2 text-right">
                              <button
                                data-testid={`history-edit-${m.key}-${r.id}`}
                                type="button"
                                onClick={(e) => { e.stopPropagation(); openEdit(r); }}
                                className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-sky-700 hover:text-white hover:bg-sky-600 border border-sky-200 hover:border-sky-600 rounded px-2 py-1 transition-colors"
                              >
                                <PencilSimple size={12} weight="bold" /> Edit
                              </button>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>
        ))}
      </Tabs>

      {/* Edit dialog — Owner koreksi data operasional */}
      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) { setEditing(null); setEditForm({}); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="history-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg flex items-center gap-2">
              <PencilSimple size={18} weight="bold" className="text-sky-600" />
              Edit {editing?.module?.label} — Koreksi Owner
            </DialogTitle>
            <DialogDescription className="text-[12px]">
              Semua perubahan akan tercatat di Edit Logs beserta waktu, nama, dan nilai sebelum/sesudah.
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-3 mt-2">
              <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-[11.5px] text-amber-900 flex gap-2">
                <Warning size={14} weight="fill" className="mt-[2px] shrink-0" />
                <span>
                  Kosongkan field yang <b>tidak</b> ingin diubah. Hanya field yang diisi yang akan disimpan.
                  Foto & status tidak bisa diedit di sini.
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {(EDIT_FIELDS[tab] || []).map((f) => (
                  <div key={f.k} className={f.type === "textarea" ? "sm:col-span-2" : ""}>
                    <label className="text-[10.5px] font-bold uppercase tracking-[0.1em] text-slate-500">{f.label}</label>
                    <div className="mt-1">
                      {f.type === "textarea" ? (
                        <Textarea
                          data-testid={`history-edit-field-${f.k}`}
                          value={editForm[f.k] ?? ""}
                          onChange={(e) => setEditForm((s) => ({ ...s, [f.k]: e.target.value }))}
                          rows={2}
                          className="text-[12px]"
                        />
                      ) : f.type === "select" ? (
                        <select
                          data-testid={`history-edit-field-${f.k}`}
                          value={editForm[f.k] ?? ""}
                          onChange={(e) => setEditForm((s) => ({ ...s, [f.k]: e.target.value }))}
                          className="w-full h-10 text-[12px] border border-slate-200 rounded-md px-3 bg-white"
                        >
                          <option value="">— pilih —</option>
                          {f.options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                        </select>
                      ) : (
                        <Input
                          data-testid={`history-edit-field-${f.k}`}
                          type={f.type}
                          value={editForm[f.k] ?? ""}
                          onChange={(e) => setEditForm((s) => ({ ...s, [f.k]: e.target.value }))}
                          className="h-10 text-[12px]"
                          step={f.type === "number" ? "any" : undefined}
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 mt-4">
            <Button
              data-testid="history-edit-cancel"
              variant="outline"
              onClick={() => { setEditing(null); setEditForm({}); }}
              disabled={saving}
              className="h-10"
            >
              Batal
            </Button>
            <Button
              data-testid="history-edit-save"
              onClick={saveEdit}
              disabled={saving}
              className="h-10 bg-sky-600 hover:bg-sky-700 gap-2"
            >
              <FloppyDisk size={14} weight="bold" /> {saving ? "Menyimpan…" : "Simpan Perubahan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg">Detail Data</DialogTitle>
            <DialogDescription className="text-[12px]">
              Semua field & foto bukti dari baris ini.
            </DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-4 mt-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[12px]">
                {Object.entries(detail.row)
                  .filter(([k, v]) => !currentMod.photoFields.includes(k) && typeof v !== "object" && v !== null && v !== "")
                  .map(([k, v]) => (
                    <div key={k} className="flex gap-2 border-b border-slate-100 py-1.5">
                      <span className="font-heading text-[10.5px] uppercase tracking-wider text-slate-500 min-w-[110px]">{k}</span>
                      <span className="font-mono text-[12px] text-slate-800 break-all">{String(v)}</span>
                    </div>
                  ))}
              </div>
              {detail.photos.length > 0 && (
                <div>
                  <div className="text-[11px] font-bold uppercase tracking-wider text-slate-600 mb-2">Foto Bukti ({detail.photos.length})</div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="history-detail-photos">
                    {detail.photos.map((p, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setLightbox(p)}
                        className="group rounded-md overflow-hidden border border-slate-200 hover:border-sky-500 aspect-video"
                      >
                        <AuthenticatedImage src={p.src} alt={p.label} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200" />
                        <div className="text-[10px] font-mono text-slate-500 py-1 px-2 text-left truncate">{p.label}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Lightbox */}
      {lightbox && (
        <div
          data-testid="history-lightbox"
          onClick={() => setLightbox(null)}
          className="fixed inset-0 z-[100] bg-black/85 flex items-center justify-center p-4 cursor-zoom-out"
        >
          <button
            data-testid="history-lightbox-close"
            onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
          >
            <X size={20} />
          </button>
          <div className="max-w-5xl max-h-[90vh]">
            <AuthenticatedImage src={lightbox.src} alt={lightbox.label} className="max-w-full max-h-[85vh] object-contain rounded-md" onClick={(e) => e.stopPropagation()} />
            <div className="text-center text-white/70 text-[12px] font-mono mt-3">{lightbox.label}</div>
          </div>
        </div>
      )}
    </AppShell>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10.5px] font-bold uppercase tracking-[0.1em] text-slate-500">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function buildColumns(module) {
  switch (module) {
    case "gate":
      return [
        { key: "created_at", label: "Waktu",   render: (r) => <span className="font-mono text-[11px]">{fmtDate(r.created_at)}</span> },
        { key: "access_type", label: "Tipe" },
        { key: "vehicle_no", label: "Plat", render: (r) => <span className="font-mono">{r.vehicle_no}</span> },
        { key: "driver_name", label: "Driver" },
        { key: "company", label: "Perusahaan" },
        { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
      ];
    case "receiving":
      return [
        { key: "created_at", label: "Waktu", render: (r) => <span className="font-mono text-[11px]">{fmtDate(r.created_at)}</span> },
        { key: "ref_no", label: "No. Ref", render: (r) => <span className="font-mono font-bold">{r.ref_no}</span> },
        { key: "supplier", label: "Supplier" },
        { key: "material", label: "Barang" },
        { key: "truck_no", label: "Plat", render: (r) => <span className="font-mono">{r.truck_no}</span> },
        { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
      ];
    case "delivery":
      return [
        { key: "created_at", label: "Waktu", render: (r) => <span className="font-mono text-[11px]">{fmtDate(r.created_at)}</span> },
        { key: "do_no", label: "No. DO", render: (r) => <span className="font-mono font-bold">{r.do_no}</span> },
        { key: "customer", label: "Customer" },
        { key: "product", label: "Produk" },
        { key: "truck_no", label: "Plat", render: (r) => <span className="font-mono">{r.truck_no}</span> },
        { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
      ];
    case "weigh":
      return [
        { key: "created_at", label: "Waktu", render: (r) => <span className="font-mono text-[11px]">{fmtDate(r.created_at)}</span> },
        { key: "slip_no", label: "Slip", render: (r) => <span className="font-mono font-bold">{r.slip_no}</span> },
        { key: "direction", label: "Arah", render: (r) => (
            <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${r.direction === "in" ? "bg-emerald-100 text-emerald-800" : "bg-orange-100 text-orange-800"}`}>
              {r.direction === "in" ? "Masuk" : "Keluar"}
            </span>
          ) },
        { key: "truck_no", label: "Plat", render: (r) => <span className="font-mono">{r.truck_no}</span> },
        { key: "weight", label: "Berat (kg)", render: (r) => <span className="font-mono">{r.weight?.toLocaleString?.("id-ID")}</span> },
        { key: "operator", label: "Operator" },
      ];
    case "loading":
      return [
        { key: "created_at", label: "Waktu", render: (r) => <span className="font-mono text-[11px]">{fmtDate(r.created_at)}</span> },
        { key: "do_no", label: "No. DO", render: (r) => <span className="font-mono font-bold">{r.do_no}</span> },
        { key: "source_tank", label: "Tank" },
        { key: "target_qty", label: "Qty", render: (r) => <span className="font-mono">{r.target_qty?.toLocaleString?.("id-ID")}</span> },
        { key: "operator", label: "Operator" },
        { key: "supervisor", label: "Supervisor" },
      ];
    case "opname":
      return [
        { key: "created_at", label: "Waktu", render: (r) => <span className="font-mono text-[11px]">{fmtDate(r.created_at)}</span> },
        { key: "opname_date", label: "Tgl Opname" },
        { key: "tank_name", label: "Tank" },
        { key: "product", label: "Produk" },
        { key: "measured_qty", label: "Terukur", render: (r) => <span className="font-mono">{r.measured_qty?.toLocaleString?.("id-ID")}</span> },
        { key: "variance_pct", label: "Varian %", render: (r) => <span className={`font-mono ${r.status === "critical" ? "text-red-700 font-bold" : r.status === "warning" ? "text-amber-700" : "text-slate-700"}`}>{r.variance_pct}%</span> },
        { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
      ];
    default:
      return [];
  }
}
